/**
 * An icon from the design system's 36-icon sprite.
 *
 * Rendered through `<use>` against an inlined sprite, so the same symbol is
 * downloaded once and drawn many times. Stroke and fill come from the sprite's
 * own CSS: `fill: none`, `stroke: currentColor`, which is why an icon takes
 * the colour of surrounding text without being told.
 *
 * Decorative by default. PRD §9.7 requires that status is never conveyed by
 * colour or shape alone, so an icon that carries meaning needs a `label` —
 * which switches it from `aria-hidden` to an accessible image.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.4, PRD §9.6, §9.7
 */
import type { IconName } from './icon-names';
import { ICON_SIZES, type IconSize } from './icon-sizes';

export interface IconProps {
  /** Which symbol to draw. */
  name: IconName;
  /** One of the design system's three sizes. Defaults to `sm`. */
  size?: IconSize;
  /**
   * An accessible name.
   *
   * Omit for a decorative icon that sits beside text saying the same thing —
   * announcing it twice is noise. Supply one when the icon is the only carrier
   * of its meaning.
   */
  label?: string;
  className?: string;
}

export function Icon({
  name,
  size = 'sm',
  label,
  className,
}: IconProps): React.JSX.Element {
  const decorative = label === undefined;

  return (
    <svg
      className={className}
      width={ICON_SIZES[size]}
      height={ICON_SIZES[size]}
      /* `focusable="false"` keeps IE-era SVGs out of the tab order; harmless
         elsewhere and still recommended for embedded sprites. */
      focusable="false"
      {...(decorative ? { 'aria-hidden': true } : { role: 'img', 'aria-label': label })}
    >
      <use href={`#i-${name}`} />
    </svg>
  );
}
